"use client";

import React, { useState, useEffect } from 'react';
import { Button } from '@vaiu/ui/button';
import { Input } from '@vaiu/ui/input';
import { Label } from '@vaiu/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@vaiu/ui/card';
import { Alert, AlertDescription } from '@vaiu/ui/alert';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { i18n } from '@/utils/i18n';
// import VaiuLogo from '@/components/shared/VaiuLogo';
import GoogleSignIn from '@/components/GoogleSignIn';
import OTPLogin from '@/components/OTPLogin';
import PhoneSignup from '@/components/PhoneSignup';
import { useAuth } from '@/context/AuthContext';
import { Lock, CheckCircle, QrCode, Store, Users, Zap, Smartphone, Mail, ArrowLeft } from 'lucide-react';
import attributionService, { AttributionData } from '@/services/attributionService';
import { platformApi } from '@/services/platformApi';
import { getDefaultAuthenticatedDestination } from '@/utils/domainDetection';

const SignupPage = () => {
  const { isGuest } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [attributionData, setAttributionData] = useState<AttributionData | null>(null);
  const [showOTPLogin, setShowOTPLogin] = useState(false);
  const [showPhoneSignup, setShowPhoneSignup] = useState(false);
  const [signupMethod, setSignupMethod] = useState<'email' | 'phone' | null>(null);
  const navigate = useNavigate();
  const { login, user, loading: authLoading } = useAuth();
  const [searchParams] = useSearchParams();

  // Capture attribution data on page load
  useEffect(() => {
    const attribution = attributionService.captureAttribution();
    setAttributionData(attribution);
    
    // Check for special attribution parameters
    const from = searchParams.get('from');
    const ref = searchParams.get('ref');
    const campaign = searchParams.get('campaign');
    
    if (from || ref || campaign) {
      const customData: Record<string, any> = {};
      if (from) customData.source_page = from;
      if (ref) customData.referral_code = ref;
      if (campaign) customData.campaign_override = campaign;
      
      attributionService.updateAttribution(customData);
      setAttributionData(prev => prev ? { ...prev, custom_data: { ...prev.custom_data, ...customData } } : null);
    }
  }, [searchParams]);

  // Redirect authenticated users to hostname default
  useEffect(() => {
    if (!authLoading && user) {
      console.log('🔄 User is already authenticated, redirecting to default destination');
      navigate(getDefaultAuthenticatedDestination());
    }
  }, [user, authLoading, navigate]);

  const calculatePasswordStrength = (password: string) => {
    if (!password) return { score: 0, label: '', color: '' };
    
    let score = 0;
    const checks = {
      length: password.length >= 8,  // Just for strength indicator, not enforced
      lowercase: /[a-z]/.test(password),
      uppercase: /[A-Z]/.test(password),
      numbers: /\d/.test(password),
      symbols: /[^A-Za-z0-9]/.test(password)
    };
    
    // Calculate score based on criteria met
    Object.values(checks).forEach(check => {
      if (check) score++;
    });
    
    // Determine strength level
    if (score === 0 && password.length > 0) {
      return { score, label: i18n.__('vaiu.password_strength.very_weak'), color: 'text-red-600', bgColor: 'bg-red-500' };
    } else if (score <= 2) {
      return { score, label: i18n.__('vaiu.password_strength.weak'), color: 'text-red-500', bgColor: 'bg-red-400' };
    } else if (score === 3) {
      return { score, label: i18n.__('vaiu.password_strength.medium'), color: 'text-orange-500', bgColor: 'bg-orange-400' };
    } else if (score === 4) {
      return { score, label: i18n.__('vaiu.password_strength.strong'), color: 'text-green-500', bgColor: 'bg-green-400' };
    } else {
      return { score, label: i18n.__('vaiu.password_strength.very_strong'), color: 'text-green-600', bgColor: 'bg-green-500' };
    }
  };

  const getPasswordIcon = (strength: ReturnType<typeof calculatePasswordStrength>) => {
    if (strength.score >= 4) {
      return <CheckCircle className="w-4 h-4 text-green-600" />;
    } else {
      return <Lock className={`w-4 h-4 ${strength.color}`} />;
    }
  };

  const validateField = (field: string, value: string) => {
    let error = '';
    
    switch (field) {
      case 'email':
        // No real-time validation - only validate on form submission
        break;
      case 'password':
        // No validation - allow any password but show strength feedback
        break;
      case 'confirmPassword':
        if (value && value !== formData.password) {
          error = i18n.__('vaiu.passwords_not_match');
        }
        break;
    }
    
    setFieldErrors(prev => ({ ...prev, [field]: error }));
    return error === '';
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError(''); // Clear global error when user types
    
    // Clear any existing field errors when user starts typing
    if (fieldErrors[field as keyof typeof fieldErrors]) {
      setFieldErrors(prev => ({ ...prev, [field]: '' }));
    }
    
    // Only validate specific fields in real-time (not email)
    if (field !== 'email') {
      setTimeout(() => {
        if (value) validateField(field, value);
        else setFieldErrors(prev => ({ ...prev, [field]: '' }));
        
        // Re-validate confirm password when password changes
        if (field === 'password' && formData.confirmPassword) {
          validateField('confirmPassword', formData.confirmPassword);
        }
      }, 300);
    }
  };

  const validateForm = () => {
    let isValid = true;
    const errors = { email: '', password: '', confirmPassword: '' };

    // Validate email (required since it's used as username)
    if (!formData.email) {
      errors.email = i18n.__('login.provide_email');
      isValid = false;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = i18n.__('vaiu.validation.invalid_email');
      isValid = false;
    }

    // Validate password
    if (!formData.password) {
      errors.password = i18n.__('login.provide_pass');
      isValid = false;
    }

    // Validate confirm password
    if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = i18n.__('vaiu.passwords_not_match');
      isValid = false;
    }

    setFieldErrors(errors);
    
    if (!isValid) {
      setError(i18n.__('vaiu.validation.fix_errors'));
    }

    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const requestBody: any = {
        username: formData.email,
        email: formData.email,
        password: formData.password,
        // Include attribution data in signup request
        attribution: attributionData,
      };

      // If user is currently a guest, include guest token to preserve UUID and data
      if (isGuest) {
        const guestToken = platformApi.getToken();
        if (guestToken) {
          requestBody.guestToken = guestToken;
        }
      }

      const response = await fetch('/api/v1/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error?.message || data.message || 'Registration failed');
      }

      setSuccess(i18n.__('vaiu.registration_success'));
      setFieldErrors({ email: '', password: '', confirmPassword: '' });
      
      // Track successful signup conversion
      attributionService.trackConversion('signup_completed', {
        email: formData.email,
        signup_method: 'email',
      });
      
      // Clear attribution data after successful signup
      attributionService.clearAttribution();
      
      // Redirect to login page after successful registration
      setTimeout(() => {
        navigate('/login?newUser=true');
      }, 2000);

    } catch (err: any) {
      const errorMessage = err.message || i18n.__('vaiu.errors.try_again_later');
      
      // Track failed signup conversion
      attributionService.trackConversion('signup_failed', {
        email: formData.email,
        signup_method: 'email',
        error: errorMessage,
      });
      
      // Map specific error messages to user-friendly i18n messages
      let friendlyError = errorMessage;
      if (errorMessage.toLowerCase().includes('username already exists') || 
          errorMessage.toLowerCase().includes('username already taken')) {
        friendlyError = i18n.__('vaiu.errors.username_already_exists');
      } else if (errorMessage.toLowerCase().includes('email address already registered') ||
                 errorMessage.toLowerCase().includes('email already') || 
                 errorMessage.toLowerCase().includes('email exists')) {
        friendlyError = i18n.__('vaiu.errors.email_already_exists');
      }
        
      setError(friendlyError);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignInSuccess = async (token: string) => {
    try {
      setLoading(true);
      setError('');
      setSuccess('');
      
      // Track successful Google signup conversion
      attributionService.trackConversion('signup_completed', {
        signup_method: 'google',
      });
      
      // Clear attribution data after successful signup
      attributionService.clearAttribution();
      
      // Store token directly and redirect immediately
      localStorage.setItem('authToken', token);
      setSuccess(i18n.__('vaiu.google_signup_success'));
      
      // Force redirect to dashboard - this avoids React Router timing issues
      window.location.href = '/dashboard';
      
    } catch (err: any) {
      console.error('Google Sign-In success handler error:', err);
      
      // Track failed Google signup conversion
      attributionService.trackConversion('signup_failed', {
        signup_method: 'google',
        error: err.message,
      });
      
      setError('Google Sign-In was successful but login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignInError = (error: string) => {
    setError(`Google Sign-In failed: ${error}`);
  };

  const handleOTPSuccess = async (token: string) => {
    try {
      setLoading(true);
      setError('');
      setSuccess('');
      
      // Track successful OTP signup conversion
      attributionService.trackConversion('signup_completed', {
        signup_method: 'otp',
      });
      
      // Clear attribution data after successful signup
      attributionService.clearAttribution();
      
      // Store token and redirect
      await login(token);
      setSuccess('Phone verification successful!');
      
      // Navigate to dashboard
      navigate(getDefaultAuthenticatedDestination(), { replace: true });
      
    } catch (err: any) {
      console.error('OTP signup error:', err);
      
      // Track failed OTP signup conversion
      attributionService.trackConversion('signup_failed', {
        signup_method: 'otp',
        error: err.message,
      });
      
      setError('Phone verification was successful but login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePhoneSignupSuccess = async (token: string) => {
    try {
      // Track successful phone signup conversion
      attributionService.trackConversion('signup_completed', {
        signup_method: 'phone',
      });
      
      // Clear attribution data after successful signup
      attributionService.clearAttribution();
      
      // Token is already set by PhoneSignup component
      // Navigate to dashboard
      navigate(getDefaultAuthenticatedDestination(), { replace: true });
      
    } catch (err: any) {
      console.error('Phone signup success handler error:', err);
    }
  };

  // If OTP login is shown, render that instead
  if (showOTPLogin) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center px-4">
        <OTPLogin
          onSuccess={handleOTPSuccess}
          onBack={() => setShowOTPLogin(false)}
        />
      </div>
    );
  }

  // If phone signup is shown, render that instead
  if (showPhoneSignup) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center px-4">
        <PhoneSignup
          onSuccess={handlePhoneSignupSuccess}
          onBack={() => setShowPhoneSignup(false)}
          onSwitchToEmail={() => {
            setShowPhoneSignup(false);
            setSignupMethod('email');
          }}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen page-background">
      {/* Logo Row - Fixed top position for mobile */}
      <div className="w-full py-6 sm:py-8 lg:py-12">
        <div className="flex justify-center">
          {/* <VaiuLogo 
            size="xl" 
            variant="wordmark" 
            showTagline={true}
            className="transition-all duration-300 hover:scale-105"
          /> */}
        </div>
      </div>
      
      {/* Main Content Row - Centered signin/join area */}
      <div className="flex-1 flex items-center justify-center px-4 pb-8">
        <div className="w-full max-w-md space-y-6">

        {/* App Description Header - COMMENTED OUT: Now handled by comprehensive home page */}
        {/*
        <div className="text-center space-y-4">
          <div className="flex justify-center">
            <VaiuLogo 
              size="xl" 
              variant="wordmark" 
              showTagline={true}
              className="transition-all duration-300 hover:scale-105"
            />
          </div>
          
          <Card className="bg-white/80 backdrop-blur-sm border-blue-200">
            <CardContent className="p-6">
              <div className="space-y-4">
                <div className="flex items-center justify-center space-x-2 mb-3">
                  <Store className="w-5 h-5 text-blue-600" />
                  <Users className="w-5 h-5 text-green-600" />
                  <Zap className="w-5 h-5 text-purple-600" />
                </div>
                
                <p className="text-gray-700 text-sm leading-relaxed">
                  {i18n.__('vaiu.app.description.main')}
                </p>
                
                <div className="text-xs text-gray-500 pt-2 border-t border-gray-200">
                  <p>
                    <strong>Joining is easy.</strong> {i18n.__('vaiu.app.description.joining')}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
        */}

        {/* Signup Card */}
        <Card className="bg-white shadow-lg">
          <CardHeader>
            <CardTitle className="text-2xl font-bold text-center">
              {i18n.__('vaiu.signup_title')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {/* Show method selection if no method chosen */}
            {!signupMethod ? (
              <div className="space-y-4">
                <div className="text-center text-sm text-muted-foreground mb-4">
                  Choose how you'd like to sign up
                </div>
                
                <Button 
                  type="button" 
                  variant="outline" 
                  className="w-full h-12 flex items-center justify-center" 
                  onClick={() => setShowPhoneSignup(true)}
                  disabled={loading}
                >
                  <Smartphone className="w-4 h-4 mr-2" />
                  Sign up with Phone Number
                </Button>

                <GoogleSignIn
                  onSuccess={handleGoogleSignInSuccess}
                  onError={handleGoogleSignInError}
                  disabled={loading}
                />

                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-white px-2 text-muted-foreground">
                      Or
                    </span>
                  </div>
                </div>

                <Button 
                  type="button" 
                  variant="outline" 
                  className="w-full h-12" 
                  onClick={() => setSignupMethod('email')}
                  disabled={loading}
                >
                  <Mail className="w-4 h-4 mr-2" />
                  Sign up with Email
                </Button>

                <div className="text-center text-sm text-gray-600 mt-4">
                  {i18n.__('vaiu.registration_page.have_account_question')}{' '}
                  <Link to="/login" className="text-blue-600 hover:underline">
                    {i18n.__('vaiu.log_in')}
                  </Link>
                </div>
              </div>
            ) : (
              /* Show email signup form when email method is selected */
              <>
            <div className="space-y-4 mb-4">
              <Button 
                type="button" 
                variant="ghost" 
                className="w-full" 
                onClick={() => setSignupMethod(null)}
                disabled={loading}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to signup options
              </Button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">{i18n.__('vaiu.email')} *</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder={i18n.__('vaiu.email')}
                  required
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  className={fieldErrors.email ? 'border-red-500' : ''}
                />
                <p className="text-xs text-gray-500">
                  {i18n.__('vaiu.signup.email_as_username_note')}
                </p>
                {fieldErrors.email && (
                  <p className="text-sm text-red-600">{fieldErrors.email}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">{i18n.__('vaiu.password')} *</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type="password"
                    placeholder={i18n.__('vaiu.password')}
                    required
                    value={formData.password}
                    onChange={(e) => handleInputChange('password', e.target.value)}
                    className={fieldErrors.password ? 'border-red-500 pr-10' : 'pr-10'}
                  />
                  {formData.password && (
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                      {getPasswordIcon(calculatePasswordStrength(formData.password))}
                    </div>
                  )}
                </div>
                
                {/* Password Strength Indicator */}
                {formData.password && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">
                        {i18n.__('vaiu.password_strength.label')}:
                      </span>
                      <span className={`text-sm font-medium ${calculatePasswordStrength(formData.password).color}`}>
                        {calculatePasswordStrength(formData.password).label}
                      </span>
                    </div>
                    
                    {/* Visual Strength Bar */}
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className={`h-2 rounded-full transition-all duration-300 ${calculatePasswordStrength(formData.password).bgColor}`}
                        style={{ width: `${(calculatePasswordStrength(formData.password).score / 5) * 100}%` }}
                      ></div>
                    </div>
                    
                    {/* Password Tips */}
                    {calculatePasswordStrength(formData.password).score < 4 && (
                      <div className="text-xs text-gray-500">
                        {i18n.__('vaiu.password_strength.tips')}
                      </div>
                    )}
                  </div>
                )}
                
                {fieldErrors.password && (
                  <p className="text-sm text-red-600">{fieldErrors.password}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">{i18n.__('vaiu.retype_password')} *</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  placeholder={i18n.__('vaiu.retype_password')}
                  required
                  value={formData.confirmPassword}
                  onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                  className={fieldErrors.confirmPassword ? 'border-red-500' : ''}
                />
                {fieldErrors.confirmPassword && (
                  <p className="text-sm text-red-600">{fieldErrors.confirmPassword}</p>
                )}
              </div>

              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              {success && (
                <Alert>
                  <AlertDescription className="text-green-700">{success}</AlertDescription>
                </Alert>
              )}

              <Button 
                type="submit" 
                className="w-full" 
                disabled={loading || !formData.email || !formData.password || !formData.confirmPassword || Object.values(fieldErrors).some(error => error !== '')}
              >
                {loading ? i18n.__('vaiu.creating_account') : i18n.__('vaiu.sign_up')}
              </Button>

              <div className="text-center text-sm text-gray-600">
                {i18n.__('vaiu.registration_page.have_account_question')}{' '}
                <Link to="/login" className="text-blue-600 hover:underline">
                  {i18n.__('vaiu.log_in')}
                </Link>
              </div>
            </form>
              </>
            )}
          </CardContent>
        </Card>
        </div>
      </div>
    </div>
  );
};

export default SignupPage; 